import { AppRoutes } from '@consts/routes';
import { useQuery } from '@hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { useOrgs } from '@hooks/useOrgs';
import { withRetry } from '@utils/helper';
import { useSelector } from 'react-redux';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useFetch } from '@hooks/useFetch';
import { getCutData, type ExportActivityParams, getWriter, cutDataCsvMaker } from '@utils/export';
import { numberFormatter, percentFormatter } from '@utils/format';
import { useStyles } from './styles/ExportActivity.styles';
import { useAlert } from '@hooks/useAlert';

import type { RootState } from '@features/redux/store';
import type { ParsedCut } from '@/types/cuts';

/**
 * The maximum number of items to fetch per page.
 */
const PAGE_SIZE = 2_000;

const enum ExportStatus {
    Ready,
    Success,
    Loading,
    Failed,
}

// ? Assume each query takes 10 seconds
const ESTIMATED_REQUEST_SECONDS = 10;

const getEstimatedExportMinutes = (totalRows: number) =>
    Math.ceil(((totalRows / PAGE_SIZE) * ESTIMATED_REQUEST_SECONDS) / 60);

export const ExportActivity = () => {
    const classes = useStyles();

    const navigate = useNavigate();
    const { setAlert } = useAlert();

    const query = useQuery();
    if (!query.get('params') || !query.get('columns')) navigate(AppRoutes.Insights);
    const params = JSON.parse(query.get('params')!) as ExportActivityParams;
    const columns = JSON.parse(query.get('columns')!) as (keyof ParsedCut)[];

    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { populated, ...orgs } = useOrgs();

    const [exportStatus, setExportStatus] = useState<ExportStatus>(ExportStatus.Ready);
    const [exportPercentage, setExportPercentage] = useState(0);

    const cutPage = getCutData(IdToken!, orgs as any);

    const { data: count } = useFetch({
        defaultValue: 0,
        fetcher: cutPage({ ...params, countOnly: 'true', pageNum: 0 }),
        modifier: ([{ totalCutsInQuery }]) => +totalCutsInQuery,
        errorHandler: () => {
            setAlert('Failed to initialize export.', 'error');
        },
    });

    const estimate = getEstimatedExportMinutes(count);

    const startExport = async () => {
        try {
            // Prompt user to pick file, create OrderedWriter for
            // writing to the file.
            const writer = getWriter(params);

            setExportStatus(ExportStatus.Loading);

            const lastPage = Math.ceil(count / PAGE_SIZE);
            // Get a list all page numbers
            const pageNums = [...Array(lastPage).keys()];

            // For each page
            await pageNums.reduce(async (acc, pageNum, index) => {
                await acc;

                // Fetch the cut data
                const items = await withRetry({
                    func: cutPage({ ...params, pageNum: pageNum, pageSize: PAGE_SIZE }),
                    attempts: 2,
                });

                // Generate CSV from the data
                const csv = cutDataCsvMaker({ items, headers: index === 0, columns });

                writer.write(csv);

                setExportPercentage((index + 1) / pageNums.length);
            }, Promise.resolve());

            // Complete writing the file, and download it
            writer.close();
            setExportStatus(ExportStatus.Success);
        } catch (error) {
            setExportStatus(ExportStatus.Failed);
        }
    };

    const handleCancel = () => window.close();

    return (
        <Box className={classes.container}>
            <Typography component='h1' fontWeight='bold'>
                {`${exportStatus === ExportStatus.Loading ? 'Exporting' : 'Export of'} ${numberFormatter.format(
                    count ?? 0
                )} cuts${exportStatus === ExportStatus.Loading ? '...' : ''}`}
            </Typography>

            {exportStatus === ExportStatus.Ready && (
                <Typography>
                    Estimated export time: <b>Up to {estimate} minutes</b>
                </Typography>
            )}

            {exportStatus === ExportStatus.Loading && (
                <>
                    <Typography fontStyle='italic'>
                        (This may take {estimate > 5 ? `up to ${estimate}` : 'a few'} minutes)
                    </Typography>
                    <Typography>{percentFormatter.format(exportPercentage)}</Typography>
                    <Box className={classes.loadingContainer}>
                        <LinearProgress variant='determinate' value={exportPercentage * 100} />
                    </Box>
                </>
            )}

            {exportStatus === ExportStatus.Success && <Typography>Export complete!</Typography>}
            {exportStatus === ExportStatus.Failed && <Typography>Export failed. Please try again.</Typography>}

            <Box className={classes.buttonContainer}>
                <Button
                    variant='contained'
                    size='large'
                    onClick={startExport}
                    disabled={
                        !count ||
                        !populated ||
                        (exportStatus !== ExportStatus.Ready && exportStatus !== ExportStatus.Failed)
                    }>
                    Start
                </Button>

                <Button
                    color={exportStatus === ExportStatus.Success ? 'primary' : 'error'}
                    size='large'
                    variant='contained'
                    onClick={handleCancel}>
                    {exportStatus === ExportStatus.Success ? 'Exit' : 'Cancel'}
                </Button>
            </Box>
        </Box>
    );
};
